import { COURSE_QUERY, ICourse } from '../entities/course.entity';
import { graphqlApi } from '../clients/graphql-api.client';
import { IGraphqlSchemaGetCourseByIdOrSlug } from '../entities/graphqlSchema.entity';
import axios from 'axios';
import { environment } from 'utils/environment.utils';
import { credentialsRepository } from 'data/repositories/credential.repository';

interface ICoursesDatasource {
    getCourseByIdOrSlug(id: string): Promise<ICourse | null>;
    setLessonCompleted(courseId: string, moduleIndex: number, lessonIndex: number): Promise<void>;
    syncCourses(courseId: string): Promise<void>;
}

class CoursesDatasource implements ICoursesDatasource {

    public async getCourseByIdOrSlug(courseId: string): Promise<ICourse | null> {
        try {
            // Fetch course data from the API
            const { data } = await graphqlApi.query<IGraphqlSchemaGetCourseByIdOrSlug>({
                query: COURSE_QUERY,
                variables: {
                    courseId,
                },
            });

            // Deep copy dos dados de curso para tornar os objetos mutáveis.
            const course = data.getCourseByIdOrSlug ? JSON.parse(JSON.stringify(data.getCourseByIdOrSlug)) : null;
            if (!course) return course;

            // Inicializa o objeto progress.
            course.progress = { value: 0, percent: 0, totalLessons: 0, completedLessons: 0 };

            // Iteração entre módulos e aulas.
            for (let moduleIndex = 0; moduleIndex < course.modules.length; moduleIndex += 1) {
                const module = course.modules[moduleIndex];
                let moduleProgress = 0;

                for (let lessonIndex = 0; lessonIndex < module.lessons.length; lessonIndex += 1) {
                    const lesson = module.lessons[lessonIndex];

                    // Checa se a aula está concluída em localStorage.
                    const isCompleted = this.isCompletedLesson(course.id, moduleIndex, lessonIndex);
                    course.progress.totalLessons += 1;

                    // Atualiza o status de complitude.
                    if (isCompleted) {
                        moduleProgress += (1 / module.lessons.length) * 100;
                        course.progress.completedLessons += 1;

                        // Atualiza o objeto com o status da junção.
                        if (!lesson.isCompleted) {
                            lesson.isCompleted = isCompleted;
                        }
                    }

                    // Configura a próxima aula se não está completo.
                    if (!isCompleted && !course.nextLesson) {
                        course.nextLesson = { moduleId: moduleIndex, lessonId: lessonIndex };
                    }
                }

                // Atualiza progresso do módulo.
                module.progress = moduleProgress;
                module.progressPercent = Math.round(moduleProgress);

                // Atualiza progresso do curso.
                if (course.progress.completedLessons) {
                    course.progress.value = (course.progress.completedLessons / course.progress.totalLessons) * 100;
                    course.progress.percent = Math.round(course.progress.value);
                }
            }

            return course;
        } catch (error) {
            console.error(`Falha em obter o curso de ID ${courseId}:`, error);
            throw error;
        }
    }

    public async setLessonCompleted(courseId: string, moduleIndex: number, lessonIndex: number): Promise<void> {
        localStorage.setItem(
            '@ToroCourseProgress',
            JSON.stringify({
                ...this.progressData,
                [`${courseId}-${moduleIndex}-${lessonIndex}`]: true,
            }),
        );

        const credentials = credentialsRepository.getCredentials();
        if (environment.API_URL && credentials) {
            await axios.post(`${environment.API_URL}/infoproduct/progress`, {
                courseId,
                moduleId: moduleIndex,
                lessonId: lessonIndex,
            }, {
                headers: {
                    Authorization: `${credentials.tokenType} ${credentials.token}`,
                },
            });
        }
    }

    public async syncCourses(courseId: string): Promise<void> {
        const { data } = await graphqlApi.query<IGraphqlSchemaGetCourseByIdOrSlug>({
            query: COURSE_QUERY,
            variables: {
                courseId,
            },
        });
        const course = data.getCourseByIdOrSlug || null;
        if (!course) return;
        try {
            for (let moduleIndex = 0; moduleIndex < course.modules.length; moduleIndex += 1) {
                const module = course.modules[moduleIndex];
                for (let lessonIndex = 0; lessonIndex < module.lessons.length; lessonIndex += 1) {
                    const lesson = module.lessons[lessonIndex];
                    const isCompleted = this.isCompletedLesson(course.id, moduleIndex, lessonIndex);
                    if (lesson.isCompleted !== isCompleted) {
                        // eslint-disable-next-line no-await-in-loop
                        await this.setLessonCompleted(course.id, moduleIndex, lessonIndex);
                    }
                }
            }
        } catch (error) {
            console.error(`Falha em sincronizar o curso de ID ${course.id}:`, error);
            throw error;
        }
    }

    /**
     * ?
     * @param courseId
     * @param moduleIndex
     * @param lessonIndex
     * @returns
     */
    private isCompletedLesson(courseId: string, moduleIndex: number, lessonIndex: number): boolean {
        return !!this.progressData[`${courseId}-${moduleIndex}-${lessonIndex}`];
    }

    /**
     * ?
     */
    private get progressData(): Record<string, boolean> {
        const localProgressRaw = localStorage.getItem('@ToroCourseProgress');
        return (localProgressRaw && JSON.parse(localProgressRaw)) || {};
    }

}

export const coursesDatasource: ICoursesDatasource = new CoursesDatasource();
